// useGpsTrail — sends a single GPS ping to the backend every PING_INTERVAL_MS
// while the page is foregrounded. Pauses when the tab is hidden to preserve
// battery. Sends one ping immediately on mount. Designed to be invoked once
// per session shell (PMC / Contractor today pages) for the active supervisor.
import { useEffect, useRef } from "react";
import { api } from "@/lib/api";

const DEFAULT_PING_INTERVAL_MS = 5 * 60 * 1000; // default 5 minutes

export function useGpsTrail({ enabled = true, parkIdRef = null } = {}) {
  const lastPingAtRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    if (typeof navigator === "undefined" || !navigator.geolocation) return;

    let cancelled = false;
    let timer = null;

    const sendOnce = () => {
      if (document.hidden) return; // pause when tab not visible
      if (Date.now() - lastPingAtRef.current < 30 * 1000) return; // safety throttle
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          if (cancelled) return;
          lastPingAtRef.current = Date.now();
          try {
            await api.post("/gps/ping", {
              lat: pos.coords.latitude,
              long: pos.coords.longitude,
              accuracy_m: pos.coords.accuracy,
              park_id: typeof parkIdRef === "string" ? parkIdRef : parkIdRef?.current || null,
            });
          } catch {
            // silent — pings are best-effort
          }
        },
        () => {
          // permission denied / timeout — skip this tick
        },
        { enableHighAccuracy: false, timeout: 12_000, maximumAge: 60_000 },
      );
    };

    // initial ping
    sendOnce();

    // Fetch tracking interval setting from backend and set interval timer
    api
      .get("/settings")
      .then((res) => {
        if (cancelled) return;
        const intervalMins = res.data?.supervisor_gps_tracking_interval_minutes || 5;
        const intervalMs = intervalMins * 60 * 1000;
        timer = setInterval(sendOnce, intervalMs);
      })
      .catch(() => {
        if (cancelled) return;
        timer = setInterval(sendOnce, DEFAULT_PING_INTERVAL_MS);
      });

    const onVisibility = () => {
      if (!document.hidden) sendOnce();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, parkIdRef]);
}
